import { Fragment, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/utils/axios";

import avatarFallback from "@/assets/UI/avatar.png";
import coinIcon from "@/assets/UI/coin.webp";
import paymentSubmissionsIcon from "@/assets/UI/paymentSubmissions.webp";
import allAccountsIcon from "@/assets/UI/allAccounts.webp";
import walletIcon from "@/assets/UI/wallet.webp";

import { formatDateTime } from "@/utils/formatDateTime";
import s from "./WalletLiabilityAccount.module.scss";

const DEFAULT_LIMIT = 100;

const GROUP_OPTIONS = [
  { value: "none", label: "No grouping" },
  { value: "week", label: "Weekly" },
  { value: "month", label: "Monthly" },
  { value: "year", label: "Yearly" },
];

const formatAmount = (value) => {
  const numeric = Number(value ?? 0);
  if (Number.isNaN(numeric)) return "0";
  return numeric.toLocaleString(undefined, {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  });
};

const getUserIdFromEntry = (entry) =>
  entry?.user?.id ||
  entry?.user?._id ||
  entry?.userId ||
  entry?.user?.userId ||
  null;

const getWeekLabel = (date) => {
  if (!date || Number.isNaN(date.getTime())) return "Unknown week";
  const start = new Date(date);
  const day = start.getDay();
  start.setDate(start.getDate() - day);
  start.setHours(0, 0, 0, 0);
  const end = new Date(start);
  end.setDate(start.getDate() + 6);

  return `Week of ${start.toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
    year: "numeric",
  })}`;
};

const buildGroupLabel = (groupBy, createdAt) => {
  const date = createdAt ? new Date(createdAt) : null;
  if (!date || Number.isNaN(date.getTime())) return "Unknown date";

  if (groupBy === "week") return getWeekLabel(date);
  if (groupBy === "month")
    return date.toLocaleString(undefined, { month: "long", year: "numeric" });
  if (groupBy === "year") return String(date.getFullYear());

  return "All transactions";
};

const WalletLiabilityAccount = () => {
  const [account, setAccount] = useState(null);
  const [entries, setEntries] = useState([]);
  const [sourceTypes, setSourceTypes] = useState([]);

  const [filters, setFilters] = useState({
    startDate: "",
    endDate: "",
    sourceType: "",
    groupBy: "none",
  });

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const fetchEntries = async () => {
    try {
      setLoading(true);
      setError("");

      const res = await api.get("/v1/admin/finance/accounts/wallet-liability", {
        params: {
          limit: DEFAULT_LIMIT,
          startDate: filters.startDate || undefined,
          endDate: filters.endDate || undefined,
          sourceType: filters.sourceType || undefined,
        },
      });

      setAccount(res.data?.account ?? null);
      setEntries(res.data?.data ?? []);
      setSourceTypes(res.data?.sourceTypes ?? []);
    } catch (err) {
      setError(
        err?.response?.data?.message ||
          err?.message ||
          "Failed to load wallet liability transactions",
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchEntries();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleChange = (field) => (event) => {
    setFilters((prev) => ({ ...prev, [field]: event.target.value }));
  };

  const applyFilters = (event) => {
    event.preventDefault();
    fetchEntries();
  };

  const resetFilters = () => {
    setFilters({ startDate: "", endDate: "", sourceType: "", groupBy: "none" });
  };

  const groupedEntries = useMemo(() => {
    if (!entries.length) return [];
    const gb = filters.groupBy;

    if (gb === "none") return [{ label: "All transactions", items: entries }];

    return entries.reduce((acc, entry) => {
      const label = buildGroupLabel(gb, entry.createdAt);
      const group = acc.find((g) => g.label === label);
      if (group) group.items.push(entry);
      else acc.push({ label, items: [entry] });
      return acc;
    }, []);
  }, [entries, filters.groupBy]);

  const renderPrettyDateTime = (value) => {
    const dt = formatDateTime(value);
    if (!dt) return <span className={s.dash}>-</span>;

    const hasParts =
      dt.day !== undefined && dt.suffix && dt.month && dt.year !== undefined;

    return (
      <div className={s.dateTime}>
        <div className={s.date}>
          {hasParts ? (
            <>
              {dt.day}
              <sup className={s.sup}>{dt.suffix}</sup> {dt.month} {dt.year}
            </>
          ) : (
            dt.date
          )}
        </div>
        <div className={s.time}>{dt.time}</div>
      </div>
    );
  };

  return (
    <div className={s.page}>
      <header className={s.header}>
        <div className={s.headerLeft}>
          <p className={s.eyebrow}>Finance • Wallet liability</p>
          <h1 className={s.title}>Wallet liability ledger</h1>
          <p className={s.subtitle}>
            Track member wallet liabilities, filter by source and date, and
            inspect each ledger entry.
          </p>
        </div>

        <div className={s.actions}>
          <Link className={s.btn} to="/admin/finance/accounts">
            <img className={s.btnIcon} src={allAccountsIcon} alt="" />
            Back
          </Link>
        </div>
      </header>

      {/* Balance */}
      <section className={s.card}>
        <div className={s.balanceHead}>
          <div className={s.balanceLeft}>
            <p className={s.muted}>Current balance</p>
            <p className={s.balanceName}>
              {account?.name || "Wallet Liability"}
            </p>
          </div>

          <div className={s.balanceGrid}>
            <div className={s.balanceMetric}>
              <p className={s.muted}>Noty</p>
              <div className={s.metric}>
                <img className={s.coinMd} src={coinIcon} alt="" />
                <span className={s.metricValue}>
                  {formatAmount(account?.balanceNoty ?? 0)}
                </span>
                <span className={s.metricUnit}>NOTY</span>
              </div>
            </div>

            <div className={s.balanceMetric}>
              <p className={s.muted}>CAD</p>
              <div className={s.metric}>
                <span className={s.cadValue}>
                  {formatAmount(account?.balanceCad ?? 0)}
                </span>
                <span className={s.metricUnit}>CAD</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Filters */}
      <section className={s.card}>
        <h2 className={s.sectionTitle}>Filters</h2>

        <form className={s.filters} onSubmit={applyFilters}>
          <label className={s.formRow}>
            <span className={s.label}>Start date</span>
            <input
              className={s.input}
              type="date"
              value={filters.startDate}
              onChange={handleChange("startDate")}
            />
          </label>

          <label className={s.formRow}>
            <span className={s.label}>End date</span>
            <input
              className={s.input}
              type="date"
              value={filters.endDate}
              onChange={handleChange("endDate")}
            />
          </label>

          <label className={s.formRow}>
            <span className={s.label}>Group by</span>
            <div className={s.selectWrap}>
              <select
                className={s.select}
                value={filters.groupBy}
                onChange={handleChange("groupBy")}
              >
                {GROUP_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
            </div>
          </label>

          <label className={s.formRow}>
            <span className={s.label}>Source type</span>
            <div className={s.selectWrap}>
              <select
                className={s.select}
                value={filters.sourceType}
                onChange={handleChange("sourceType")}
              >
                <option value="">All sources</option>
                {sourceTypes.map((source) => (
                  <option key={source} value={source}>
                    {source}
                  </option>
                ))}
              </select>
            </div>
          </label>

          <div className={s.inlineActions}>
            <button className={`${s.btn} ${s.btnPrimary}`} type="submit">
              Apply
            </button>
            <button
              className={`${s.btn} ${s.btnSecondary}`}
              type="button"
              onClick={resetFilters}
            >
              Reset
            </button>
          </div>
        </form>
      </section>

      {error ? (
        <div className={`${s.stateBox} ${s.stateBoxError}`}>
          <strong>Something went wrong</strong>
          <span>{error}</span>
        </div>
      ) : null}

      {/* Transactions */}
      <section className={s.card}>
        <h2 className={s.sectionTitle}>Transactions</h2>

        {loading ? (
          <div className={s.stateBox}>Loading wallet liability entries…</div>
        ) : entries.length === 0 ? (
          <div className={s.stateBox}>
            No wallet liability transactions found.
          </div>
        ) : (
          <div className={s.tableWrap}>
            <table className={s.table}>
              <thead>
                <tr>
                  <th>Member</th>
                  <th>Amount</th>
                  <th>Date</th>
                  <th>Balance</th>
                  <th className={s.thActions}>Actions</th>
                </tr>
              </thead>

              <tbody>
                {groupedEntries.map((group) => (
                  <Fragment key={group.label}>
                    <tr className={s.groupRow}>
                      <td colSpan={6}>
                        <div className={s.groupRowInner}>
                          <strong>{group.label}</strong>
                          <span className={s.groupCount}>
                            {group.items.length} transactions
                          </span>
                        </div>
                      </td>
                    </tr>

                    {group.items.map((entry) => {
                      const isCredit = entry.entryType === "credit";
                      const userId = getUserIdFromEntry(entry);
                      const memberPath = userId
                        ? `/admin/members/single/${userId}`
                        : null;

                      const walletPath = entry.walletId
                        ? `/admin/finance/wallets/${entry.walletId}`
                        : null;

                      const viewPath = `/admin/finance/accounts/wallet-liability/transactions/${entry.id}`;

                      return (
                        <tr key={entry.id} className={s.row}>
                          <td data-label="Member">
                            {memberPath ? (
                              <Link to={memberPath} className={s.memberLink}>
                                <span className={s.avatarRing}>
                                  <img
                                    src={entry.avatar?.url || avatarFallback}
                                    alt=""
                                    className={s.avatarLg}
                                  />
                                </span>

                                <span className={s.memberText}>
                                  <span className={s.userName}>
                                    {entry.user?.username || "Unknown user"}
                                  </span>

                                  <div className={s.reasonCell}>
                                    <div className={s.reasonText}>
                                      {entry.reason || "-"}
                                    </div>
                                    <div className={s.reasonMeta}>
                                      <span
                                        className={`${s.typeBadge} ${
                                          isCredit ? s.typeCredit : s.typeDebit
                                        }`}
                                      >
                                        {isCredit ? "credit" : "debit"}
                                      </span>
                                    </div>
                                  </div>
                                </span>
                              </Link>
                            ) : (
                              <div className={s.memberLink}>
                                <span className={s.avatarRing}>
                                  <img
                                    src={entry.avatar?.url || avatarFallback}
                                    alt=""
                                    className={s.avatarLg}
                                  />
                                </span>

                                <span className={s.memberText}>
                                  <span className={s.userName}>
                                    {entry.user?.username || "Unknown user"}
                                  </span>

                                  <div className={s.reasonCell}>
                                    <div className={s.reasonText}>
                                      {entry.reason || "-"}
                                    </div>
                                    <div className={s.reasonMeta}>
                                      <span
                                        className={`${s.typeBadge} ${
                                          isCredit ? s.typeCredit : s.typeDebit
                                        }`}
                                      >
                                        {isCredit ? "credit" : "debit"}
                                      </span>
                                    </div>
                                  </div>
                                </span>
                              </div>
                            )}
                          </td>
                         
                          <td data-label="Amount (Noty)">
                            <div
                              className={`${s.amount} ${
                                isCredit ? s.amountCredit : s.amountDebit
                              }`}
                            >
                              <span className={s.sign}>
                                {isCredit ? "+" : "-"}
                              </span>
                              <span className={s.amountValue}>
                                {formatAmount(entry.amountNoty)}
                              </span>
                              <img className={s.coinSm} src={coinIcon} alt="" />
                            </div>
                          </td>

                          <td data-label="Date" className={s.dateCell}>
                            {renderPrettyDateTime(entry.createdAt)}
                          </td>

                          <td data-label="Balance after">
                            <div className={s.balanceAfter}>
                              <span className={s.balanceAfterValue}>
                                {formatAmount(entry.balanceAfterNoty ?? 0)}
                              </span>
                              <img className={s.coinSm} src={coinIcon} alt="" />
                            </div>
                          </td>

                          <td data-label="Actions" className={s.actionsCell}>
                            <div className={s.actionStack}>
                              {walletPath ? (
                                <Link
                                  className={`${s.iconBtn} ${s.iconBtnAlt}`}
                                  to={walletPath}
                                  title="View wallet"
                                  aria-label="View wallet"
                                >
                                  <img
                                    className={s.iconBtnImg}
                                    src={walletIcon}
                                    alt=""
                                  />
                                </Link>
                              ) : (
                                <span
                                  className={`${s.iconBtn} ${s.iconBtnDisabled}`}
                                  title="Wallet missing"
                                  aria-label="Wallet missing"
                                >
                                  <img
                                    className={s.iconBtnImg}
                                    src={walletIcon}
                                    alt=""
                                  />
                                </span>
                              )}
                              <Link
                                className={s.iconBtn}
                                to={viewPath}
                                title="View entry"
                                aria-label="View entry"
                              >
                                <img
                                  className={s.iconBtnImg}
                                  src={paymentSubmissionsIcon}
                                  alt=""
                                />
                              </Link>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </Fragment>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </div>
  );
};

export default WalletLiabilityAccount;
